import { VuexModule, Module, Action, Mutation } from "vuex-module-decorators";
import _ from "lodash";
import OffersApi from "@/api/offers-api";
import InstallationsApi from "@/api/installation-api";
import { installationsModule } from "./store-accessor";
import { deepTrim } from "@/helpers/data";

@Module({ namespaced: true, name: "offers" })
export default class OfferModule extends VuexModule {
  public offers: OfferDto[] = [];
  public offersPagination: PaginationDto = { total: 0, page: 1, limit: 20, pages: 0 };
  public isLoadingTable = true;


  @Action
  async getAllOffers(query: string): Promise<OfferDto[]> {
    const offerApi = new OffersApi();
    this.setIsLoadingTable(true);

    try {
      const response = await offerApi.getAllOffers(query) as OfferDto[] | OfferResponseDto;
      const offers = Array.isArray(response) ? response : response.data;

      this.setOffers(offers);
      this.setOffersPagination(Array.isArray(response) ? {
        total: offers.length,
        page: 1,
        limit: offers.length,
        pages: offers.length > 0 ? 1 : 0,
      }
      : response.pagination);

      return offers;
    } finally {
      this.setIsLoadingTable(false);
    }
  }

  @Action
  async getOfferPdfExportAction(data: Record<string, any>) {
    const offerApi = new OffersApi();
    const response = await offerApi.getOfferPdfExport(data);
    const blob = new Blob([response], { type: "application/pdf" });
    return blob;
  }

  @Action
  async createOffer(data: Record<string, any>): Promise<OfferDto> {
    const offerApi = new OffersApi();

    data.assignee_id = data.assignee_id ? data.assignee_id : null;

    data.valid_until = data.valid_until ? data.valid_until : null;

    data = deepTrim(data);

    const offer = await offerApi.createOffer(data);

    if (!offer?.id) {
      throw new Error('The offer could not be created.');
    }

    if (data.installation_id) {
      await installationsModule.getInstallationOffers(Number(data.installation_id));
      await installationsModule.makeInstallationSideviewCommonCalls(String(data.installation_id));
    }

    return offer;

  }

  @Action
  async createOfferPhoto({ installationId, offerId, photo, refresh = true }: { installationId: string, offerId: string, photo: FormData, refresh?: boolean }) {
    const installationsApi = new InstallationsApi();
    const response = await installationsApi.createOfferPhoto(installationId, offerId, photo);

    if (refresh) {
      await this.refreshOfferPhotos({ installationId, offerId });
    }

    if (response === undefined) {
      throw new Error('The offer photos could not be uploaded.');
    }

    return response;
  }

  @Action
  async cloneOfferPhotos({ installationId, offerId, sourceOfferId, photoIds, refresh = true }: { installationId: string, offerId: string, sourceOfferId: string, photoIds: string[], refresh?: boolean }) {
    const installationsApi = new InstallationsApi();
    const response = await installationsApi.cloneOfferPhotos(installationId, offerId, {
      source_offer_id: sourceOfferId,
      photo_ids: photoIds,
    });

    if (response === undefined) {
      throw new Error('The offer photos could not be cloned.');
    }

    if (refresh) {
      await this.refreshOfferPhotos({ installationId, offerId });
    }

    return response;
  }

  @Action
  async getOfferPhotos({ installationId, offerId }: { installationId: string, offerId: string }): Promise<OfferPhotoDto[]> {
    const installationsApi = new InstallationsApi();
    return installationsApi.getOfferPhotos(installationId, offerId);
  }

  @Action
  async getOfferPhoto({ installationId, offerId, photoId }: { installationId: string, offerId: string, photoId: string }): Promise<Blob> {
    const installationsApi = new InstallationsApi();
    const response = await installationsApi.getOfferPhoto(installationId, offerId, photoId);
    return new Blob([response]);
  }

  @Action
  async updateOfferPhoto({ installationId, offerId, photoId, notes }: { installationId: string, offerId: string, photoId: string, notes: string }) {
    const installationsApi = new InstallationsApi();
    const response = await installationsApi.updateOfferPhoto(installationId, offerId, photoId, { notes: deepTrim(notes) });
    await this.refreshOfferPhotos({ installationId, offerId });
    return response;
  }

  @Action
  async deleteOfferPhoto({ installationId, offerId, photoId }: { installationId: string, offerId: string, photoId: string }) {
    const installationsApi = new InstallationsApi();
    await installationsApi.deleteOfferPhoto(installationId, offerId, photoId);
    await this.refreshOfferPhotos({ installationId, offerId });
  }

  @Action
  async refreshOfferPhotos({ installationId, offerId }: { installationId: string, offerId: string }) {
    await Promise.all([
      installationsModule.getInstallationOffers(Number(installationId)),
      installationsModule.getInstallationOfferDetails({ installationId, offerId }),
      installationsModule.makeInstallationSideviewCommonCalls(String(installationId)),
    ]);
  }

  @Action
  async createOfferLineItem(data: Record<string, any> | Record<string, any>[]) {
    const offerApi = new OffersApi();

    data = deepTrim(data);

    const lineItems = Array.isArray(data) ? data[0] : data;

    await offerApi.createOfferLineItem(data);

    if (lineItems.installation_id) {
      await installationsModule.getInstallationOffers(Number(lineItems.installation_id));
      await installationsModule.makeInstallationSideviewCommonCalls(String(lineItems.installation_id));
    }

    if (lineItems.installation_id && lineItems.offerId) {
      await installationsModule.getInstallationOfferDetails({ installationId: lineItems.installation_id, offerId: lineItems.offerId });
    }

  }

  @Action
  async deleteOfferLineItem(data: Record<string, any>) {
    const offerApi = new OffersApi();

    await offerApi.deleteOfferLineItem(data);

    if (data.installationId) {
      await installationsModule.getInstallationOffers(data.installationId);
      await installationsModule.getInstallationOfferDetails({ installationId: data.installationId, offerId: data.offerId });
      await installationsModule.makeInstallationSideviewCommonCalls(String(data.installationId));
    }
  }

  @Action
  async updateOfferLineItem(data: Record<string, any>) {
    let dataPayload = _.cloneDeep(data);

    dataPayload.action_type_id = dataPayload.action_type?.id;
    dataPayload.time_unit = dataPayload.time_unit?.unit ? dataPayload.time_unit?.unit : null;

    dataPayload.inventory_unit = dataPayload.inventory_unit?.enum_text ? dataPayload.inventory_unit?.enum_text : null;

    dataPayload.discount_percentage = dataPayload.discount_percentage ? Number(Number(dataPayload.discount_percentage || 0).toFixed(2)) : 0;

    dataPayload = deepTrim(dataPayload);

    const offerApi = new OffersApi();

    await offerApi.updateOfferLineItem(dataPayload);

    if (dataPayload.installation_id) {
      await installationsModule.getInstallationOffers(dataPayload.installation_id);
      await installationsModule.makeInstallationSideviewCommonCalls(String(dataPayload.installation_id)
      );
    }

    if (dataPayload.installation_id && dataPayload.offer_id) {
      await installationsModule.getInstallationOfferDetails({ installationId: dataPayload.installation_id, offerId: dataPayload.offer_id, });
    }

  }

  @Action
  async getOffersPdfExportAction({ query, data }: { query: string; data: Record<string, any> }) {
    const offerApi = new OffersApi();

    const responseType = data.format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

    const response = await offerApi.getOffersPdfExportAction(query, data);

    const blob = new Blob([response], { type: responseType });

    return blob;
  }

  @Mutation
  public setOffers(offers: OfferDto[]): void {
    this.offers = offers;
  }

  @Mutation
  public setOffersPagination(pagination: PaginationDto): void {
    this.offersPagination = pagination;
  }

  @Mutation
  public setIsLoadingTable(isLoadingTable: boolean): void {
    this.isLoadingTable = isLoadingTable;
  }

  @Mutation
  public clearOffers(): void {
    this.offers = [];
    this.offersPagination = { total: 0, page: 1, limit: 20, pages: 0 };
  }

}
